import type {ArrWaveformOptions} from './arrWaveform';
import EventEmitter from './event-emitter';
import {effect} from './reactive/arr-waveform-store';
import {createDragStream} from './reactive/drag-stream';
import {createScrollStream} from './reactive/scroll-stream';
import * as utils from './renderer-utils';

type RendererEvents = {
  click: [relativeX: number, relativeY: number];
  dblclick: [relativeX: number, relativeY: number];
  drag: [relativeX: number];
  dragstart: [relativeX: number];
  dragend: [relativeX: number];
  scroll: [
    relativeStart: number,
    relativeEnd: number,
    scrollLeft: number,
    scrollRight: number,
  ];
  render: [];
  rendered: [];
  resize: [];
};

class Renderer extends EventEmitter<RendererEvents> {
  private options: ArrWaveformOptions;
  private parent: HTMLElement;
  private container: HTMLElement;
  private scrollContainer: HTMLElement;
  private wrapper: HTMLElement;
  private canvasWrapper: HTMLElement;
  private progressWrapper: HTMLElement;
  private cursor: HTMLElement;
  private timeouts: Array<() => void> = [];
  private isScrollable = false;

  private resizeObserver: ResizeObserver | null = null;
  private lastContainerWidth = 0;
  private isDragging = false;
  private subscriptions: (() => void)[] = [];
  private unsubscribeOnScroll: (() => void)[] = [];
  private dragStream: {signal: any; cleanup: () => void} | null = null;
  private scrollStream: {
    scrollData: any;
    percentages: any;
    bounds: any;
    cleanup: () => void;
  } | null = null;

  constructor(options: ArrWaveformOptions, audioElement?: HTMLElement) {
    super();

    this.subscriptions = [];
    this.options = options;

    const parent = this.parentFromOptionsContainer(options.container);
    this.parent = parent;

    const [div, shadow] = this.initHtml();
    parent.appendChild(div);
    this.container = div;
    this.scrollContainer = shadow.querySelector('.scroll') as HTMLElement;

    this.wrapper = shadow.querySelector('.wrapper') as HTMLElement;
    this.canvasWrapper = shadow.querySelector('.canvases') as HTMLElement;
    this.progressWrapper = shadow.querySelector('.progress') as HTMLElement;
    this.cursor = shadow.querySelector('.cursor') as HTMLElement;

    if (audioElement) {
      shadow.appendChild(audioElement);
    }

    this.initEvents();
  }

  private parentFromOptionsContainer(
    container: ArrWaveformOptions['container']
  ) {
    let parent;
    if (typeof container === 'string') {
      parent = document.querySelector(container) satisfies HTMLElement | null;
    } else if (container instanceof HTMLElement) {
      parent = container;
    }

    if (!parent) {
      throw new Error('Container not found');
    }

    return parent;
  }

  private initEvents() {
    this.wrapper.addEventListener('click', (e) => {
      const rect = this.wrapper.getBoundingClientRect();
      const [x, y] = utils.getRelativePointerPosition(
        rect,
        e.clientX,
        e.clientY
      );
      this.emit('click', x, y);
    });

    this.wrapper.addEventListener('dblclick', (e) => {
      const rect = this.wrapper.getBoundingClientRect();
      const [x, y] = utils.getRelativePointerPosition(
        rect,
        e.clientX,
        e.clientY
      );
      this.emit('dblclick', x, y);
    });

    if (
      this.options.dragToSeek === true ||
      typeof this.options.dragToSeek === 'object'
    ) {
      this.initDrag();
    }

    this.scrollStream = createScrollStream(this.scrollContainer);
    const unsubscribeScroll = effect(() => {
      const {startX, endX} = this.scrollStream!.percentages.value;
      const {left, right} = this.scrollStream!.bounds.value;
      this.emit('scroll', startX, endX, left, right);
    }, [this.scrollStream.percentages, this.scrollStream.bounds]);
    this.subscriptions.push(unsubscribeScroll);

    if (typeof ResizeObserver === 'function') {
      const delay = this.createDelay(100);
      this.resizeObserver = new ResizeObserver(() => {
        delay()
          .then(() => this.onContainerResize())
          .catch(() => undefined);
      });
      this.resizeObserver.observe(this.scrollContainer);
    }
  }

  private onContainerResize() {
    const width = this.parent.clientWidth;
    if (width === this.lastContainerWidth && this.options.height !== 'auto')
      return;
    this.lastContainerWidth = width;
    this.reRender();
    this.emit('resize');
  }

  private initDrag() {
    if (this.dragStream) return;

    this.dragStream = createDragStream(this.wrapper);

    const unsubscribeDrag = effect(() => {
      const drag = this.dragStream!.signal.value;
      if (!drag) return;

      const width = this.wrapper.getBoundingClientRect().width;
      const relX = utils.clampToUnit(drag.x / width);

      if (drag.type === 'start') {
        this.isDragging = true;
        this.emit('dragstart', relX);
      } else if (drag.type === 'move') {
        this.emit('drag', relX);
      } else if (drag.type === 'end') {
        this.isDragging = false;
        this.emit('dragend', relX);
      }
    }, [this.dragStream.signal]);

    this.subscriptions.push(unsubscribeDrag);
  }

  private initHtml(): [HTMLElement, ShadowRoot] {
    const div = document.createElement('div');
    const shadow = div.attachShadow({mode: 'open'});

    const cspNonce =
      this.options.cspNonce && typeof this.options.cspNonce === 'string'
        ? this.options.cspNonce.replace(/"/g, '')
        : '';

    shadow.innerHTML = `
      <style${cspNonce ? ` nonce="${cspNonce}"` : ''}>
        :host {
          user-select: none;
          min-width: 1px;
        }
        :host audio {
          display: block;
          width: 100%;
        }
        :host .scroll {
          overflow-x: auto;
          overflow-y: hidden;
          width: 100%;
          position: relative;
          scroll-behavior: smooth;
          scrollbar-width: thin;
          scrollbar-color: $scrollbars-primary-page transparent;
          &::-webkit-scrollbar {
            width: $size-0-25rem;
            background: $glacier-surface-intense;
          }
          &::-webkit-scrollbar-track {
            background: $glacier-surface-intense;
            border-radius: $size-0-125rem;
          }
          &::-webkit-scrollbar-thumb {
            background-color: $scrollbars-primary-page;
            border-radius: $size-0-125rem;
          }
          &::-webkit-scrollbar-button {
            background-color: transparent;
            display: none;
          }
        }
        :host .noScrollbar {
          scrollbar-color: transparent;
          scrollbar-width: none;
        }
        :host .noScrollbar::-webkit-scrollbar {
          display: none;
          -webkit-appearance: none;
        }
        :host .wrapper {
          position: relative;
          overflow: visible;
          z-index: 2;
          background-size: 100% 100%;
          background-position: left top;
          background-repeat: no-repeat;
          cursor: pointer;
        }
        :host .canvases {
          min-height: ${this.getHeight(this.options.height, this.options.splitChannels)}px;
          pointer-events: none;
        }
        :host .canvases > div {
          position: relative;
        }
        :host canvas {
          display: block;
          position: absolute;
          top: 0;
          image-rendering: pixelated;
        }
        :host .progress {
          pointer-events: none;
          position: absolute;
          z-index: 2;
          top: 0;
          left: 0;
          width: 0;
          height: 100%;
          overflow: hidden;
          transition: width 200ms ease-out;
        }
        :host .progress > div {
          position: relative;
        }
        :host .cursor {
          pointer-events: none;
          position: absolute;
          isolation: isolate;
          z-index: 5;
          top: 0;
          left: 0;
          height: 100%;
          transition: left 200ms ease-out;
        }
      </style>

      <div class="scroll" part="scroll">
        <div class="wrapper" part="wrapper">
          <div class="canvases" part="canvases"></div>
          <div class="progress" part="progress"></div>
          <div class="cursor" part="cursor"></div>
        </div>
      </div>
    `;

    return [div, shadow];
  }

  setOptions(options: ArrWaveformOptions) {
    if (this.options.container !== options.container) {
      const newParent = this.parentFromOptionsContainer(options.container);
      newParent.appendChild(this.container);

      this.parent = newParent;
    }

    if (
      options.dragToSeek === true ||
      typeof this.options.dragToSeek === 'object'
    ) {
      this.initDrag();
    }

    this.options = options;

    if (this.options.waveformImageUrl) {
      this.wrapper.style.backgroundImage = `url(${this.options.waveformImageUrl})`;
    }

    this.reRender();
  }

  getWrapper(): HTMLElement {
    return this.wrapper;
  }

  getWidth(): number {
    return this.scrollContainer.clientWidth;
  }

  getScroll(): number {
    return this.scrollContainer.scrollLeft;
  }

  setScroll(pixels: number) {
    this.scrollContainer.scrollLeft = pixels;
  }

  setScrollPercentage(percent: number) {
    const {scrollWidth} = this.scrollContainer;
    const scrollStart = scrollWidth * percent;
    this.setScroll(scrollStart);
  }

  destroy() {
    this.subscriptions.forEach((unsubscribe) => unsubscribe());
    this.container.remove();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.unsubscribeOnScroll?.forEach((unsubscribe) => unsubscribe());
    this.unsubscribeOnScroll = [];
    if (this.dragStream) {
      this.dragStream.cleanup();
      this.dragStream = null;
    }
    if (this.scrollStream) {
      this.scrollStream.cleanup();
      this.scrollStream = null;
    }
  }

  private createDelay(delayMs = 10): () => Promise<void> {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let rejectFn: (() => void) | undefined;

    const onClear = () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = undefined;
      }
      if (rejectFn) {
        rejectFn();
        rejectFn = undefined;
      }
    };

    this.timeouts.push(onClear);

    return () => {
      return new Promise<void>((resolve, reject) => {
        onClear();

        rejectFn = reject;

        timeout = setTimeout(() => {
          timeout = undefined;
          rejectFn = undefined;
          resolve();
        }, delayMs);
      });
    };
  }

  private getHeight(
    optionsHeight?: ArrWaveformOptions['height'],
    optionsSplitChannel?: ArrWaveformOptions['splitChannels']
  ): number {
    const numberOfChannels = 1;
    return utils.resolveChannelHeight({
      optionsHeight,
      optionsSplitChannels: optionsSplitChannel,
      parentHeight: this.parent.clientHeight,
      numberOfChannels,
      defaultHeight: utils.DEFAULT_HEIGHT,
    });
  }

  private getPixelRatio(): number {
    return utils.getPixelRatio(window.devicePixelRatio);
  }

  async render(duration: number) {
    this.timeouts.forEach((clear) => clear());
    this.timeouts = [];

    this.canvasWrapper.innerHTML = '';
    this.progressWrapper.innerHTML = '';

    if (this.options.width != null) {
      this.scrollContainer.style.width =
        typeof this.options.width === 'number'
          ? `${this.options.width}px`
          : this.options.width;
    }

    const pixelRatio = this.getPixelRatio();
    const parentWidth = this.scrollContainer.clientWidth;
    const {scrollWidth, isScrollable, useParentWidth} =
      utils.calculateWaveformLayout({
        duration,
        minPxPerSec: this.options.minPxPerSec || 0,
        parentWidth,
        fillParent: this.options.fillParent,
        pixelRatio,
      });

    this.isScrollable = isScrollable;
    this.wrapper.style.width = useParentWidth ? '100%' : `${scrollWidth}px`;
    this.progressWrapper.style.backgroundColor = `${this.options.progressColor}`;

    this.cursor.style.backgroundColor = `${this.options.cursorColor || this.options.progressColor}`;
    this.cursor.style.width = `${this.options.cursorWidth}px`;

    this.emit('render');
    Promise.resolve().then(() => this.emit('rendered'));
  }

  reRender() {
    this.unsubscribeOnScroll.forEach((unsubscribe) => unsubscribe());
    this.unsubscribeOnScroll = [];

    const {scrollWidth} = this.scrollContainer;
    const {right: before} = this.progressWrapper.getBoundingClientRect();

    this.render(this.options.duration ?? 0);

    if (this.isScrollable && scrollWidth !== this.scrollContainer.scrollWidth) {
      const {right: after} = this.progressWrapper.getBoundingClientRect();
      const delta = utils.roundToHalfAwayFromZero(after - before);
      this.scrollContainer.scrollLeft += delta;
    }
  }

  zoom(minPxPerSec: number) {
    this.options.minPxPerSec = minPxPerSec;
    this.reRender();
  }

  private scrollIntoView(progress: number, isPlaying = false) {
    const {scrollLeft, scrollWidth, clientWidth} = this.scrollContainer;
    const progressWidth = progress * scrollWidth;
    const startEdge = scrollLeft;
    const endEdge = scrollLeft + clientWidth;
    const middle = clientWidth / 2;

    if (this.isDragging) {
      const minGap = 30;
      if (progressWidth + minGap > endEdge) {
        this.scrollContainer.scrollLeft += minGap;
      } else if (progressWidth - minGap < startEdge) {
        this.scrollContainer.scrollLeft -= minGap;
      }
    } else {
      if (progressWidth < startEdge || progressWidth > endEdge) {
        this.scrollContainer.scrollLeft =
          progressWidth - (this.options.autoCenter ? middle : 0);
      }

      const center = progressWidth - scrollLeft - middle;
      if (isPlaying && this.options.autoCenter && center > 0) {
        this.scrollContainer.scrollLeft += center;
      }
    }
  }

  renderProgress(progress: number, isPlaying?: boolean) {
    if (isNaN(progress)) return;
    const percents = progress * 100;

    this.progressWrapper.style.width = `${percents}%`;
    this.cursor.style.left = `calc(${percents}% - ${
      percents > 0 ? this.options.cursorWidth || 0 : 0
    }px)`;

    if (this.isScrollable && this.options.autoScroll) {
      this.scrollIntoView(progress, isPlaying);
    }
  }
}

export default Renderer;
